import { useState, useEffect } from 'react'
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUsers, FiUserPlus, FiUserMinus, FiClock, FiMapPin, FiStar, FiSearch, FiCheck } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { useAuth } from '../contexts/AuthContext'
import { PageHeader, Btn } from '../ui'

const DIAS_LABEL = { 1: 'L', 2: 'M', 3: 'X', 4: 'J', 5: 'V', 6: 'S', 7: 'D' }

export default function GestionGrupos() {
  const { empleado } = useAuth()
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()

  const [grupos, setGrupos] = useState([])
  const [calendarios, setCalendarios] = useState([])
  const [centros, setCentros] = useState([])
  const [empleados, setEmpleados] = useState([])
  const [loading, setLoading] = useState(true)

  const [modalGrupo, setModalGrupo] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState({ nombre: '', descripcion: '', calendario_id: '', centro_trabajo_id: '', manager_empleado_id: '' })

  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null)
  const [miembros, setMiembros] = useState([])
  const [loadingMiembros, setLoadingMiembros] = useState(false)
  const [modalAsignar, setModalAsignar] = useState(false)
  const [seleccionados, setSeleccionados] = useState([])
  const [busqueda, setBusqueda] = useState('')
  const [asignando, setAsignando] = useState(false)

  const buildUrl = (path) => {
    const sep = path.includes('?') ? '&' : '?'
    return esGestion && empresaGestionId ? `${API_URL}${path}${sep}empresa_id=${empresaGestionId}` : `${API_URL}${path}`
  }
  const token = () => localStorage.getItem('token')
  const headers = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' })

  useEffect(() => {
    Promise.all([cargarGrupos(), cargarCalendarios(), cargarCentros(), cargarEmpleados()])
  }, [empresaGestionId])

  const cargarGrupos = async () => {
    setLoading(true)
    try {
      const r = await fetch(buildUrl('/grupos-empleados'), { headers: headers() })
      if (r.ok) setGrupos(await r.json())
    } catch { toast.error('Error al cargar grupos') }
    finally { setLoading(false) }
  }

  const cargarCalendarios = async () => {
    try {
      const r = await fetch(buildUrl('/calendarios-laborales'), { headers: headers() })
      if (r.ok) setCalendarios(await r.json())
    } catch {}
  }

  const cargarCentros = async () => {
    try {
      const r = await fetch(buildUrl('/centros-trabajo'), { headers: headers() })
      if (r.ok) setCentros(await r.json())
    } catch {}
  }

  const cargarEmpleados = async () => {
    try {
      const r = await fetch(buildUrl('/empleados'), { headers: headers() })
      if (r.ok) { const d = await r.json(); setEmpleados(d.empleados || []) }
    } catch {}
  }

  const cargarMiembros = async (grupoId) => {
    setLoadingMiembros(true)
    try {
      const r = await fetch(buildUrl(`/grupos-empleados/${grupoId}/empleados`), { headers: headers() })
      if (r.ok) setMiembros(await r.json())
    } catch {}
    finally { setLoadingMiembros(false) }
  }

  const seleccionarGrupo = (grupo) => {
    setGrupoSeleccionado(grupo)
    cargarMiembros(grupo.id)
  }

  const abrirModal = (grupo = null) => {
    setEditando(grupo)
    setForm(grupo ? {
      nombre: grupo.nombre, descripcion: grupo.descripcion || '',
      calendario_id: grupo.calendario_id || '', centro_trabajo_id: grupo.centro_trabajo_id || '',
      manager_empleado_id: grupo.manager_empleado_id || ''
    } : { nombre: '', descripcion: '', calendario_id: '', centro_trabajo_id: '', manager_empleado_id: '' })
    setModalGrupo(true)
  }

  const guardar = async (e) => {
    e.preventDefault()
    const body = {
      ...form,
      calendario_id: form.calendario_id ? parseInt(form.calendario_id) : null,
      centro_trabajo_id: form.centro_trabajo_id ? parseInt(form.centro_trabajo_id) : null,
      manager_empleado_id: form.manager_empleado_id ? parseInt(form.manager_empleado_id) : null
    }
    try {
      const url = editando ? buildUrl(`/grupos-empleados/${editando.id}`) : buildUrl('/grupos-empleados')
      const r = await fetch(url, { method: editando ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(body) })
      if (r.ok) {
        toast.success(editando ? 'Grupo actualizado' : 'Grupo creado')
        setModalGrupo(false)
        cargarGrupos()
        if (grupoSeleccionado?.id === editando?.id) {
          const updated = await r.json()
          setGrupoSeleccionado(updated)
        }
      } else { const err = await r.json(); toast.error(err.error || 'Error al guardar') }
    } catch { toast.error('Error de conexión') }
  }

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este grupo?')) return
    try {
      const r = await fetch(buildUrl(`/grupos-empleados/${id}`), { method: 'DELETE', headers: headers() })
      if (r.ok) {
        toast.success('Grupo eliminado')
        if (grupoSeleccionado?.id === id) setGrupoSeleccionado(null)
        cargarGrupos()
      } else { const err = await r.json(); toast.error(err.error || 'Error al eliminar') }
    } catch { toast.error('Error de conexión') }
  }

  const toggleDefault = async (grupo) => {
    const nuevoEstado = !grupo.es_default
    try {
      const r = await fetch(buildUrl(`/grupos-empleados/${grupo.id}/default`), {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ es_default: nuevoEstado })
      })
      if (r.ok) {
        toast.success(nuevoEstado ? `"${grupo.nombre}" es ahora el grupo por defecto` : 'Grupo por defecto desmarcado')
        cargarGrupos()
      } else { const err = await r.json(); toast.error(err.error || 'Error') }
    } catch { toast.error('Error de conexión') }
  }

  const abrirModalAsignar = () => {
    setSeleccionados([])
    setBusqueda('')
    setModalAsignar(true)
  }

  const toggleSeleccion = (empId) => {
    setSeleccionados(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    )
  }

  const seleccionarTodos = () => {
    const filtrados = empleadosFiltrados.map(e => e.id)
    const todosYaSeleccionados = filtrados.every(id => seleccionados.includes(id))
    if (todosYaSeleccionados) {
      setSeleccionados(prev => prev.filter(id => !filtrados.includes(id)))
    } else {
      setSeleccionados(prev => [...new Set([...prev, ...filtrados])])
    }
  }

  const asignarSeleccionados = async () => {
    if (seleccionados.length === 0) return
    setAsignando(true)
    try {
      const r = await fetch(buildUrl(`/grupos-empleados/${grupoSeleccionado.id}/empleados/bulk`), {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ empleado_ids: seleccionados })
      })
      const data = await r.json()
      if (r.ok) {
        toast.success(`${data.asignados} empleado(s) asignado(s) al grupo`)
        cargarMiembros(grupoSeleccionado.id)
        cargarEmpleados()
        cargarGrupos()
        setModalAsignar(false)
      } else { toast.error(data.error || 'Error al asignar') }
    } catch { toast.error('Error de conexión') }
    finally { setAsignando(false) }
  }

  const desasignarEmpleado = async (empleadoId, nombre) => {
    if (!window.confirm(`¿Quitar a ${nombre} de este grupo?`)) return
    try {
      const r = await fetch(buildUrl(`/grupos-empleados/${grupoSeleccionado.id}/empleados/${empleadoId}`), {
        method: 'DELETE', headers: headers()
      })
      if (r.ok) { toast.success('Empleado desvinculado'); cargarMiembros(grupoSeleccionado.id); cargarEmpleados(); cargarGrupos() }
      else { const err = await r.json(); toast.error(err.error || 'Error') }
    } catch { toast.error('Error de conexión') }
  }

  // Empleados disponibles (no en este grupo)
  const empleadosDisponibles = empleados.filter(e =>
    e.rol !== 'super_admin' && !miembros.some(m => m.id === e.id)
  )

  const empleadosFiltrados = empleadosDisponibles.filter(e => {
    if (!busqueda) return true
    const q = busqueda.toLowerCase()
    return e.nombre?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q)
  })

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ G1"
        kicker="Grupos"
        title="Equipos por"
        italic="calendario."
        sub="Organiza empleados en grupos con calendario y centro de trabajo comunes."
        actions={<Btn variant="primary" size="sm" onClick={() => abrirModal()}><FiPlus size={14} /> Nuevo grupo</Btn>}
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de grupos */}
        <div className="lg:col-span-1">
          <div className="card p-4">
            <h2 className="font-semibold text-[var(--ink-2)] mb-3">Grupos ({grupos.length})</h2>
            {loading ? (
              <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-20 bg-[var(--bg-2)] rounded animate-pulse" />)}</div>
            ) : grupos.length === 0 ? (
              <p className="text-[var(--ink-4)] text-sm text-center py-8">No hay grupos definidos</p>
            ) : (
              <div className="space-y-2">
                {grupos.map(g => (
                  <div
                    key={g.id}
                    onClick={() => seleccionarGrupo(g)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${grupoSeleccionado?.id === g.id ? 'border-blue-500 bg-blue-50' : 'border-[var(--line)] hover:border-[var(--line-2)] hover:bg-[var(--bg-2)]'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-[var(--ink)] truncate">{g.nombre}</p>
                          {g.es_default === 1 && (
                            <span className="flex-shrink-0 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-medium">Por defecto</span>
                          )}
                        </div>
                        {g.descripcion && <p className="text-xs text-[var(--ink-4)] truncate">{g.descripcion}</p>}
                        <div className="mt-1 space-y-0.5">
                          {g.calendario_nombre && (
                            <div className="flex items-center gap-1 text-xs text-blue-600">
                              <FiClock size={10} /> {g.calendario_nombre}
                            </div>
                          )}
                          {g.centro_trabajo_nombre && (
                            <div className="flex items-center gap-1 text-xs text-green-600">
                              <FiMapPin size={10} /> {g.centro_trabajo_nombre}
                            </div>
                          )}
                          {g.manager_nombre && (
                            <div className="flex items-center gap-1 text-xs text-purple-600">
                              <FiUsers size={10} /> Manager: {g.manager_nombre}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-[var(--ink-4)] mt-1">{g.num_empleados} empleado(s)</p>
                      </div>
                      <div className="flex gap-1 ml-2 flex-shrink-0">
                        <button
                          onClick={e => { e.stopPropagation(); toggleDefault(g) }}
                          className={`p-1 rounded ${g.es_default === 1 ? 'text-yellow-500 hover:text-yellow-600' : 'text-[var(--ink-4)] hover:text-yellow-500'}`}
                          title={g.es_default === 1 ? 'Quitar como predeterminado' : 'Marcar como predeterminado'}
                        >
                          <FiStar size={13} fill={g.es_default === 1 ? 'currentColor' : 'none'} />
                        </button>
                        <button onClick={e => { e.stopPropagation(); abrirModal(g) }} className="p-1 text-[var(--ink-4)] hover:text-blue-500"><FiEdit2 size={13} /></button>
                        <button onClick={e => { e.stopPropagation(); eliminar(g.id) }} className="p-1 text-[var(--ink-4)] hover:text-red-500"><FiTrash2 size={13} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Panel de miembros */}
        <div className="lg:col-span-2">
          {!grupoSeleccionado ? (
            <div className="card p-8 flex flex-col items-center justify-center text-[var(--ink-4)] h-64">
              <FiUsers size={40} className="mb-3 opacity-30" />
              <p>Selecciona un grupo para ver y gestionar sus miembros</p>
            </div>
          ) : (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-[var(--ink)]">{grupoSeleccionado.nombre}</h2>
                    {grupoSeleccionado.es_default === 1 && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-medium">Por defecto</span>
                    )}
                  </div>
                  <div className="flex gap-3 mt-1">
                    {grupoSeleccionado.calendario_nombre && (
                      <span className="text-xs text-blue-600 flex items-center gap-1"><FiClock size={10} /> {grupoSeleccionado.calendario_nombre}</span>
                    )}
                    {grupoSeleccionado.centro_trabajo_nombre && (
                      <span className="text-xs text-green-600 flex items-center gap-1"><FiMapPin size={10} /> {grupoSeleccionado.centro_trabajo_nombre}</span>
                    )}
                  </div>
                </div>
                <button onClick={abrirModalAsignar} className="btn btn-secondary text-sm flex items-center gap-1">
                  <FiUserPlus size={14} /> Añadir empleados
                </button>
              </div>

              {loadingMiembros ? (
                <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-[var(--bg-2)] rounded animate-pulse" />)}</div>
              ) : miembros.length === 0 ? (
                <div className="text-center py-10 text-[var(--ink-4)]">
                  <FiUsers size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Este grupo no tiene miembros</p>
                  <p className="text-xs mt-1">Añade empleados usando el botón superior</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {miembros.map(emp => (
                    <div key={emp.id} className="flex items-center justify-between p-3 rounded-lg border border-[var(--line)] hover:bg-[var(--bg-2)]">
                      <div>
                        <p className="font-medium text-[var(--ink)] text-sm">{emp.nombre}</p>
                        <p className="text-xs text-[var(--ink-3)]">{emp.email}</p>
                        {emp.calendario_override_nombre && (
                          <p className="text-xs text-purple-600 mt-0.5 flex items-center gap-1">
                            <FiClock size={10} /> Calendario personal: {emp.calendario_override_nombre}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`badge ${emp.rol === 'admin' ? 'badge-purple' : 'badge-primary'} text-xs`}>
                          {emp.rol === 'admin' ? 'Admin' : 'Empleado'}
                        </span>
                        <button onClick={() => desasignarEmpleado(emp.id, emp.nombre)} className="p-1.5 text-[var(--ink-4)] hover:text-red-500" title="Quitar del grupo">
                          <FiUserMinus size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal grupo */}
      {modalGrupo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold">{editando ? 'Editar grupo' : 'Nuevo grupo'}</h3>
              <button onClick={() => setModalGrupo(false)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>
            <form onSubmit={guardar} className="p-5 space-y-4">
              <div>
                <label className="label">Nombre *</label>
                <input required value={form.nombre} onChange={e => setForm(p => ({...p, nombre: e.target.value}))} className="input w-full" placeholder="Ej: Equipo Madrid" />
              </div>
              <div>
                <label className="label">Descripción</label>
                <input value={form.descripcion} onChange={e => setForm(p => ({...p, descripcion: e.target.value}))} className="input w-full" placeholder="Descripción opcional" />
              </div>
              <div>
                <label className="label">Calendario laboral</label>
                <select value={form.calendario_id} onChange={e => setForm(p => ({...p, calendario_id: e.target.value}))} className="input w-full">
                  <option value="">— Sin calendario asignado —</option>
                  {calendarios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Centro de trabajo</label>
                <select value={form.centro_trabajo_id} onChange={e => setForm(p => ({...p, centro_trabajo_id: e.target.value}))} className="input w-full">
                  <option value="">— Sin centro asignado —</option>
                  {centros.map(c => <option key={c.id} value={c.id}>{c.nombre}{c.direccion ? ` (${c.direccion})` : ''}</option>)}
                </select>
                <p className="text-xs text-[var(--ink-3)] mt-1">
                  Define los <strong>festivos aplicables</strong> a los empleados de este grupo. Sin centro, los empleados del grupo no tendrán festivos asignados.
                </p>
              </div>
              <div>
                <label className="label">Manager del grupo</label>
                <select value={form.manager_empleado_id} onChange={e => setForm(p => ({...p, manager_empleado_id: e.target.value}))} className="input w-full">
                  <option value="">— Sin manager —</option>
                  {empleados.filter(e => e.activo).map(e => <option key={e.id} value={e.id}>{e.nombre} ({e.email})</option>)}
                </select>
                <p className="text-xs text-[var(--ink-3)] mt-1">El manager puede fichar por todos los empleados del grupo desde "Fichaje grupal".</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalGrupo(false)} className="btn btn-secondary flex-1">Cancelar</button>
                <button type="submit" className="btn btn-primary flex-1">{editando ? 'Guardar cambios' : 'Crear grupo'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal asignar empleados (bulk) */}
      {modalAsignar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="text-lg font-semibold">Añadir empleados al grupo</h3>
                <p className="text-xs text-[var(--ink-3)] mt-0.5">Selecciona uno o varios empleados para asignar</p>
              </div>
              <button onClick={() => setModalAsignar(false)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>

            {/* Barra de búsqueda */}
            <div className="px-5 pt-4 pb-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)]" size={16} />
                <input
                  type="text"
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  className="input w-full pl-9"
                  placeholder="Buscar por nombre o email..."
                />
              </div>
            </div>

            {/* Seleccionar todos + contador */}
            <div className="px-5 py-2 flex items-center justify-between border-b">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">
                <input
                  type="checkbox"
                  checked={empleadosFiltrados.length > 0 && empleadosFiltrados.every(e => seleccionados.includes(e.id))}
                  onChange={seleccionarTodos}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                Seleccionar todos
              </label>
              {seleccionados.length > 0 && (
                <span className="text-sm font-medium text-blue-600">{seleccionados.length} seleccionado(s)</span>
              )}
            </div>

            {/* Lista de empleados */}
            <div className="overflow-y-auto flex-1 p-4">
              {empleadosFiltrados.length === 0 ? (
                <p className="text-[var(--ink-4)] text-sm text-center py-6">
                  {busqueda ? 'No se encontraron empleados' : 'No hay empleados disponibles para asignar'}
                </p>
              ) : (
                <div className="space-y-1">
                  {empleadosFiltrados.map(emp => (
                    <label
                      key={emp.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        seleccionados.includes(emp.id)
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-[var(--line)] hover:border-[var(--line-2)] hover:bg-[var(--bg-2)]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={seleccionados.includes(emp.id)}
                        onChange={() => toggleSeleccion(emp.id)}
                        className="w-4 h-4 text-blue-600 rounded flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[var(--ink)] text-sm truncate">{emp.nombre}</p>
                        <p className="text-xs text-[var(--ink-3)] truncate">{emp.email}</p>
                      </div>
                      {emp.grupo_id && (
                        <span className="text-xs text-orange-500 flex-shrink-0">Cambiará de grupo</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Footer con botón de acción */}
            <div className="p-4 border-t bg-[var(--bg-2)] rounded-b-xl flex items-center justify-between">
              <button onClick={() => setModalAsignar(false)} className="btn btn-secondary">Cancelar</button>
              <button
                onClick={asignarSeleccionados}
                disabled={seleccionados.length === 0 || asignando}
                className="btn btn-primary flex items-center gap-2"
              >
                <FiCheck size={16} />
                {asignando ? 'Asignando...' : `Asignar ${seleccionados.length > 0 ? `(${seleccionados.length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
